// @ts-nocheck
import ImageCarousel from '../../../../components/Itineraries/templates/Trips/CherryOutput/ImageCarousel/ImageCarousel';
import ServiceCards from './Cards/index';
import { useEffect, useRef, useState } from 'react';
import {
  getBucketFileUrl,
  getBucketImageUrl
} from '../../../../utils/resourcePathHelper';
import { pathReference, imageSize } from '../../../../constants/files';
import { Paperclip, X, SignOut } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  useMediaQuery
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import * as P from '@phosphor-icons/react';
import './ServiceCard.css';

// Map service types to their respective card components
const ServiceComponent = {
  ACCO: ServiceCards.HotelCard,
  ACTV: ServiceCards.ActivityCard,
  CAR: ServiceCards.CarCard,
  FLTS: ServiceCards.FlightCard,
  REST: ServiceCards.RestaurantCard,
  TRN: ServiceCards.TrainCard,
  TRAN: ServiceCards.TransferCard
};

const ServiceCard = ({ service = {}, tripid, tenantid }) => {
  const { t } = useTranslation(['services']);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const scrollYBeforeOpenRef = useRef(0);

  const getAttachementUrl = (fileName = '') => {
    return getBucketFileUrl({
      fileName,
      pathReference: pathReference.DETAILED_DAY_SERVICE,
      tripId: tripid,
      tenantId: tenantid
    });
  };

  const getImageUrl = (fileName = '') => {
    return getBucketImageUrl({
      fileName,
      pathReference: pathReference.DETAILED_DAY_SERVICE,
      tripId: tripid,
      tenantId: tenantid,
      sizePrefix: imageSize.thumbnail.prefix
    });
  };

  const getFullImageUrl = (fileName = '') => {
    return getBucketImageUrl({
      fileName,
      pathReference: pathReference.DETAILED_DAY_SERVICE,
      tripId: tripid,
      tenantId: tenantid,
      sizePrefix: imageSize.medium.prefix
    });
  };

  const {
    tripdetaileddayserviceid,
    servicefields = [],
    servicetype = {},
    isCheckout = false
  } = service;
  const { iconref } = servicetype;

  const fields = servicefields?.reduce((acc, field) => {
    let values = field?.options?.map((opt) => opt?.value) || [];

    // If only one option, store it as a single value instead of an array
    values = values.length > 1 ? values : values[0] || '';

    // Ensure 'MEALSINCLUDED' is always an array
    if (field.key === 'MEALSINCLUDED' && !Array.isArray(values)) {
      values = values ? [values] : [];
    }

    // Transform 'UPLOADIMAGES' field values to image URLs
    if (field.key === 'UPLOADIMAGES') {
      const imageValues = Array.isArray(values)
        ? values
        : values
          ? [values]
          : [];
      values = {
        thumbnails: imageValues
          .map((value) => getImageUrl(value))
          .filter(Boolean),
        fullSize: imageValues
          .map((value) => getFullImageUrl(value))
          .filter(Boolean)
      };
    }

    // Transform 'UPLOADATTACHMENTS' field values to URLs
    if (field.key === 'UPLOADATTACHMENTS') {
      const attachments = Array.isArray(values)
        ? values
        : values
          ? [values]
          : [];

      const files = field?.files || [];

      values = attachments
        .map((fileName, index) => {
          return {
            url: getAttachementUrl(fileName),
            originalName: files?.[index]?.originalname
          };
        })
        .filter(Boolean);
    }

    acc[field.key] = values;
    return acc;
  }, {});

  const hastAttachments = fields?.UPLOADATTACHMENTS?.length > 0;

  const iconExists = (iconName) => {
    return typeof P[iconName] === 'object';
  };

  const ServiceTypeIcon = iconExists(iconref) ? P[iconref] : null;
  const CardComponent = ServiceComponent[servicetype?.code] ?? null;

  const handleCardClick = (event) => {
    // Check if the click came from within the image gallery
    if (event.target.closest('.image-carousel-container')) {
      return; // Don't open dialog if clicking on image gallery
    }

    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  useEffect(() => {
    if (!isDialogOpen) return;

    const body = document.body;
    scrollYBeforeOpenRef.current = window.scrollY || 0;

    const previousStyles = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollYBeforeOpenRef.current}px`;
    body.style.width = '100%';

    return () => {
      body.style.position = previousStyles.position;
      body.style.top = previousStyles.top;
      body.style.width = previousStyles.width;

      window.scrollTo(0, scrollYBeforeOpenRef.current);
    };
  }, [isDialogOpen]);

  return (
    <>
      <div
        className={`activity-card ${isCheckout ? 'activity-card-checkout' : ''}`}
        onClick={isCheckout ? null : handleCardClick}
        style={{ cursor: isCheckout ? 'default' : 'pointer' }}
      >
        <div className='activity-card__wrapper'>
          {/* Left Section - Icon and Time */}
          <div className='activity-card__left'>
            {isCheckout ? (
              <div className='activity-card-checkout__icon'>
                <SignOut size={20} />
              </div>
            ) : (
              <div className='activity-card__icon'>
                {ServiceTypeIcon && <ServiceTypeIcon size={20} />}
              </div>
            )}
          </div>
          {/* Main Content */}
          <div className='activity-card__content'>
            {CardComponent && !isCheckout ? (
              <CardComponent
                servicefields={fields}
                servicetype={servicetype}
                tripdetaileddayserviceid={tripdetaileddayserviceid}
                tripid={tripid}
                tenantid={tenantid}
                isPreview={true}
                isDesktop={isDesktop}
              />
            ) : (
              <div>
                <h3 className='activity-card__title'>
                  {t('Service:checkOutLabel', 'Check Out')}
                </h3>
                <p className='activity-card__description'>
                  {servicefields?.[1]?.value || ''}
                </p>
              </div>
            )}
          </div>

          {/* Right Section - Attachments icon */}
          {hastAttachments && (
            <div className='activity-card__right'>
              <Paperclip size={26} />
            </div>
          )}
        </div>

        {/* Images Gallery */}
        {fields?.UPLOADIMAGES && (
          <div className='activity-card__images-section'>
            {fields?.UPLOADIMAGES?.thumbnails?.map((image, index) => (
              <div className='activity-card__image-primary' key={index}>
                <img
                  src={image}
                  alt={`Image ${index + 1}`}
                  className='activity-card__image'
                  draggable={false}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Service Details Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth='md'
        fullWidth
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh',
            margin: '16px'
          }
        }}
      >
        {/* Dialog Header */}
        <DialogTitle sx={{ p: 0, position: 'relative' }}>
          <Box
            sx={{
              p: 3,
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
              gap: '.75rem'
            }}
          >
            <Box
              component='h2'
              sx={{
                m: 0,
                flex: 1,
                pr: 6
              }}
            >
              {fields?.TITLE || ''}
            </Box>
            <IconButton
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: '#333',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,1)'
                }
              }}
            >
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent className='activity-card__dialog-content'>
          {/* Images Gallery */}
          {fields?.UPLOADIMAGES && (
            <div className='activity-card__dialog-images'>
              <ImageCarousel images={fields?.UPLOADIMAGES?.fullSize || []} />
            </div>
          )}

          {/* Service Details */}
          <Box sx={{ mb: 3 }}>
            {CardComponent && (
              <CardComponent
                servicefields={fields}
                servicetype={servicetype}
                tripdetaileddayserviceid={tripdetaileddayserviceid}
                tripid={tripid}
                tenantid={tenantid}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

ServiceCard.propTypes = {
  service: PropTypes.object.isRequired,
  tripid: PropTypes.number,
  tenantid: PropTypes.number
};

export default ServiceCard;
